import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Badge,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { Play24Regular, ArrowSync24Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';

interface JobRun {
  id: string;
  jobName: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: 'running' | 'succeeded' | 'failed' | 'skipped_locked';
  trigger: 'scheduled' | 'manual';
  summary: any;
  error: string | null;
}

const KNOWN_JOBS = ['ach_reconciliation'];

const useStyles = makeStyles({
  container: { paddingTop: '16px' },
  toolbar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  tableWrapper: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  summaryCell: {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: tokens.colorNeutralForeground2,
    maxWidth: '320px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '12px',
  },
});

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return formatBusinessDateTime(iso);
}

function statusBadge(status: JobRun['status']) {
  const map: Record<JobRun['status'], { color: any; label: string }> = {
    running: { color: 'brand', label: 'Running' },
    succeeded: { color: 'success', label: 'Succeeded' },
    failed: { color: 'danger', label: 'Failed' },
    skipped_locked: { color: 'warning', label: 'Skipped' },
  };
  const cfg = map[status] || { color: 'subtle', label: status };
  return <Badge appearance="filled" color={cfg.color}>{cfg.label}</Badge>;
}

export default function Jobs() {
  const styles = useStyles();
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);

  const loadRuns = async () => {
    try {
      const res = await apiFetch(`/jobs/runs?limit=200`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load runs');
      setRuns(data.runs);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
    // Poll every 10s while page is open so a freshly-triggered run shows up
    // without a manual refresh. Cheap (read-only, indexed query).
    const id = setInterval(loadRuns, 10000);
    return () => clearInterval(id);
  }, []);

  const triggerJob = async (jobName: string) => {
    setTriggering(jobName);
    try {
      // Admin-triggered runs authenticate with the operator's admin JWT —
      // backend's /jobs/<name>/run accepts either CRON_SECRET (Vercel) or
      // a valid admin token. No client-side secret needed.
      const res = await apiFetch(`/jobs/${jobName}/run?trigger=manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok && res.status !== 200) {
        throw new Error(data.error || `Trigger failed (${res.status})`);
      }
      await loadRuns();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTriggering(null);
    }
  };

  const columns: ColumnConfig<JobRun>[] = [
    {
      columnKey: 'jobName',
      label: 'Job',
      render: (r) => <strong>{r.jobName}</strong>,
    },
    {
      columnKey: 'status',
      label: 'Status',
      render: (r) => statusBadge(r.status),
    },
    {
      columnKey: 'trigger',
      label: 'Trigger',
      render: (r) => r.trigger,
    },
    {
      columnKey: 'startedAt',
      label: 'Started',
      render: (r) => formatTime(r.startedAt),
    },
    {
      columnKey: 'durationMs',
      label: 'Duration',
      render: (r) => formatDuration(r.durationMs),
    },
    {
      columnKey: 'summary',
      label: 'Summary / Error',
      render: (r) => {
        if (r.error) {
          return <span className={styles.errorText}>{r.error.split('\n')[0]}</span>;
        }
        if (r.summary) {
          return <span className={styles.summaryCell}>{JSON.stringify(r.summary)}</span>;
        }
        return '—';
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        {KNOWN_JOBS.map((job) => (
          <Button
            key={job}
            icon={triggering === job ? <Spinner size="tiny" /> : <Play24Regular />}
            disabled={triggering !== null}
            onClick={() => triggerJob(job)}
          >
            Run {job}
          </Button>
        ))}
        <Button icon={<ArrowSync24Regular />} onClick={loadRuns}>Refresh</Button>
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>}
      {error && (
        <div style={{ padding: 16, marginBottom: 16, backgroundColor: '#fef0f0', border: '1px solid #fcd4d4', borderRadius: 4, color: '#c42b1c' }}>
          {error}
        </div>
      )}

      {!loading && (
        <div className={styles.tableWrapper}>
          <DataTable
            data={runs}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="jobs"
            itemsPerPage={50}
          />
        </div>
      )}
    </div>
  );
}
